import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import { ACCEPT_IMAGE_MIMETYPE, MAX_IMAGE_FILE_SIZE, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useImageValidator } from '@/app/hooks';
import type { ImageUploadFileInfo, SelectedImagePrompt, UploadResizeFile } from '@/app/types';
import { resizeSourceImage, uploadFile } from '@/app/utils/client';

export interface UploadErrorInfo {
  failReason: string;
  metadata?: {
    audit_id?: string;
  };
}

interface UseImageUploadOptions {
  maxFileSize?: number;
  maxCount?: number;
  isInitialSelectedImageRange?: boolean;
  scene?: UploadScene; // 上传场景，agent 用 'agent'，默认为 'vidu'
  onUploadStart?: (ids: string[]) => void;
  onUploadSuccess?: (images: UploadedImage[]) => void;
  onUploadError?: (error: UploadErrorInfo, tempId?: string) => void;
}

export interface UploadedImage extends Required<Omit<SelectedImagePrompt, 'selectedImageRange'>> {
  id?: string;
  uploadId?: string;
}

interface ImageUploadResult {
  uploading: boolean;
  selectImages: () => void;
  handleFilesChange: (files: FileList) => void;
}

/**
 * 通用的图片上传 Hook
 *
 * 提供图片文件选择、验证、上传的完整流程
 * 与 UI 解耦，可在任何需要图片上传的地方使用
 * 支持单图和多图上传
 *
 * @example
 * ```tsx
 * const { uploading, selectImages } = useImageUpload({
 *   maxCount: 3,
 *   onUploadSuccess: (images) => {
 *     console.log('图片上传成功:', images);
 *   },
 *   onUploadError: (error) => {
 *     console.error('图片上传失败:', error);
 *   }
 * });
 *
 * // 触发图片选择
 * <button onClick={selectImages}>选择图片</button>
 * ```
 */
export const useImageUpload = ({
  maxFileSize = MAX_IMAGE_FILE_SIZE,
  maxCount = 1,
  scene = UPLOAD_SCENE.VIDU,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
}: UseImageUploadOptions = {}): ImageUploadResult => {
  const t = useTranslations();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { validateImage } = useImageValidator({ maxFileSize });

  const processResizeFile = async (
    file: File,
  ): Promise<
    | {
        imageURL: string;
        imageResolution: { width: number; height: number };
        selectedImage: { blob: Blob; width: number; height: number } | null;
      }
    | { error: string; reason: string }
  > => {
    // 验证图片
    const validationResult = await validateImage(file);

    if (!validationResult.valid) {
      const errorMessage = validationResult.errorMessage || 'Unknown error';
      const errorReason = validationResult.errorReason || 'unknown';
      toast({ type: 'error', message: errorMessage });
      return { error: errorMessage, reason: errorReason };
    }
    const { imageURL, imageResolution } = validationResult;
    try {
      // 获取选中的图片区域
      const selectedImage = await resizeSourceImage({ url: imageURL, maxImageSize: maxFileSize });

      return {
        imageURL,
        imageResolution,
        selectedImage,
      };
    } catch {
      const errorMessage = t('popup.toast.uploadingError');
      toast({ type: 'error', message: errorMessage });
      return { error: errorMessage, reason: 'processingError' };
    }
  };

  /**
   * 创建初始上传信息
   */
  const createUploadInfo = (): ImageUploadFileInfo => ({
    image_resolution: '',
    pic_type: '',
    is_success: false,
    fail_reason: '',
    upload_time: 0,
  });

  /**
   * 处理调整大小的图片上传流程
   */
  const processResizeImageUpload = async (file: File, batchIndex = 0) => {
    const id = uuidv4();
    const fileUploadInfo = createUploadInfo();
    const startUploadTime = performance.now();

    // 通知上传开始
    handleUploadChange({
      id,
      file: null,
      status: 'uploading',
      batchIndex,
    });

    // 1. 处理文件（验证）
    const processedResult = await processResizeFile(file);

    if ('error' in processedResult) {
      fileUploadInfo.fail_reason = processedResult.reason;
      handleResizeImageUploadError(id, fileUploadInfo);
      return;
    }

    const { imageURL, imageResolution, selectedImage } = processedResult;
    fileUploadInfo.image_resolution = `${imageResolution.width}x${imageResolution.height}`;

    if (!selectedImage) {
      return;
    }

    // 2. 执行上传
    const resizeImageResult = await uploadFile({
      file: selectedImage.blob,
      width: selectedImage.width,
      height: selectedImage.height,
      scene,
    });

    // 处理取消情况
    if (resizeImageResult.cancelled) {
      return;
    }

    // 检查上传错误
    if (resizeImageResult.error || !resizeImageResult) {
      fileUploadInfo.fail_reason = resizeImageResult?.error?.reason || 'uploadingError';
      if (resizeImageResult?.error?.metadata?.audit_id) {
        fileUploadInfo.audit_id = resizeImageResult.error.metadata.audit_id;
      }
      handleResizeImageUploadError(id, fileUploadInfo);
      return;
    }

    // 3. 更新成功状态
    fileUploadInfo.is_success = true;
    fileUploadInfo.upload_time = performance.now() - startUploadTime;
    const previewImage = resizeImageResult.file ? URL.createObjectURL(resizeImageResult.file) : undefined;

    handleUploadChange(
      {
        id,
        file: {
          selectedId: resizeImageResult.id || '',
          selectedImage: resizeImageResult.uri,
          selectedImagePreview: previewImage || imageURL,
          selectedImageWidth: resizeImageResult.width || imageResolution.width,
          selectedImageHeight: resizeImageResult.height || imageResolution.height,
        },
        status: 'done',
      },
      fileUploadInfo,
      resizeImageResult.id || '',
    );

    if (previewImage) {
      URL.revokeObjectURL(imageURL);
    }
  };

  /**
   * 处理调整大小图片的上传错误
   */
  const handleResizeImageUploadError = (id: string, fileUploadInfo: ImageUploadFileInfo) => {
    handleUploadChange(
      {
        id,
        file: null,
        status: 'error',
      },
      fileUploadInfo,
    );
  };

  const handleUploadChange = useCallback(
    (file: UploadResizeFile, fileUploadInfo?: ImageUploadFileInfo, uploadId?: string) => {
      if (file.status === 'uploading') {
        onUploadStart?.([file.id]);
        return;
      }
      if (file.status === 'done' && file.file) {
        onUploadSuccess?.([{ id: file.id, ...file.file, uploadId: uploadId }]);
        return;
      }
      if (file.status === 'error') {
        const errorInfo: UploadErrorInfo = {
          failReason: fileUploadInfo?.fail_reason || 'Unknown error',
          metadata: fileUploadInfo?.audit_id ? { audit_id: fileUploadInfo.audit_id } : undefined,
        };
        onUploadError?.(errorInfo, file.id);
        return;
      }
    },
    [onUploadStart, onUploadSuccess, onUploadError],
  );

  /**
   * 处理多个文件上传
   */
  const handleFilesChange = useCallback(
    async (files: FileList) => {
      if (files.length === 0) return;

      let fileArray: File[] = Array.from(files);
      // 如果文件数量超过限制，则只上传前 maxCount 个文件
      if (fileArray.length > maxCount) {
        fileArray = fileArray.slice(0, maxCount);
      }

      setUploading(true);

      try {
        await Promise.all(fileArray.map((file) => processResizeImageUpload(file)));
      } finally {
        setUploading(false);
      }
    },
    [maxCount, processResizeImageUpload],
  );

  /**
   * 触发文件选择器
   */
  const selectImages = useCallback(() => {
    // 如果已经存在 input 元素，移除它
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
    }

    // 创建新的 input 元素
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT_IMAGE_MIMETYPE.join(',');
    input.multiple = maxCount > 1;
    input.style.display = 'none';

    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        await handleFilesChange(files);
      }
      // 清理 input 元素
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };

    document.body.appendChild(input);
    fileInputRef.current = input;
    input.click();
  }, [maxCount, handleFilesChange]);

  return {
    uploading,
    selectImages,
    handleFilesChange,
  };
};
